import { FigureGroupDataDto } from "./FigureGroupDataDto";
import { FigureVarSettingsDto, isFigureVarSettingsDto } from "./FigureVarSettingsDto";

/**
 * Defines the settings for a specific figure type, including available properties, axis requirements, and series defaults.
 */
export interface FigureTypeSettingsDto {
  value: string;
  label: string;
  properties: FigureVarSettingsDto[];
  dataRowVars: FigureVarSettingsDto[];
  requiredAxis: string[];
  allowedRegressionTypes?: string[];
  defaultNumberOfSeries?: number;
  defaultSeries?: Partial<FigureGroupDataDto>[];
  isLegendVisible?: boolean;
}

/**
 * Type guard to check if an object is a FigureTypeSettingsDto value.
 *
 * @param obj - The object to check.
 * @returns True when the value matches the figure type settings shape.
 */
export const isFigureTypeSettingsDto = (obj: unknown): obj is FigureTypeSettingsDto => {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }
  const chkObj = obj as FigureTypeSettingsDto;
  if (typeof chkObj.value !== "string") {
    return false;
  }
  if (typeof chkObj.label !== "string") {
    return false;
  }
  if (!Array.isArray(chkObj.properties)) {
    return false;
  }
  if (chkObj.properties.some((property) => !isFigureVarSettingsDto(property))) {
    return false;
  }
  if (!Array.isArray(chkObj.dataRowVars)) {
    return false;
  }
  if (chkObj.dataRowVars.some((property) => !isFigureVarSettingsDto(property))) {
    return false;
  }
  if (!Array.isArray(chkObj.requiredAxis)) {
    return false;
  }
  if (chkObj.requiredAxis.some((name) => typeof name !== "string")) {
    return false;
  }
  if (
    chkObj.allowedRegressionTypes !== undefined &&
    (!Array.isArray(chkObj.allowedRegressionTypes) ||
      chkObj.allowedRegressionTypes.some((type) => typeof type !== "string"))
  ) {
    return false;
  }
  if (
    chkObj.defaultNumberOfSeries !== undefined &&
    (!Number.isFinite(chkObj.defaultNumberOfSeries) ||
      !Number.isInteger(chkObj.defaultNumberOfSeries) ||
      chkObj.defaultNumberOfSeries < 0)
  ) {
    return false;
  }
  if (chkObj.defaultSeries !== undefined && !Array.isArray(chkObj.defaultSeries)) {
    return false;
  }
  if (chkObj.isLegendVisible !== undefined && typeof chkObj.isLegendVisible !== "boolean") {
    return false;
  }
  return true;
};
